import {
  useGetOwnKPIDetailsQuery,
  useGetTeamKPIDetailsQuery,
} from "@/app/features/employee/performance/performance.api";
import CustomDrawer from "@/components/CustomDrawer";
import { cn } from "@/lib/cn";
import WithLoader from "@/components/WithLoader";
import { PaperclipIcon } from "@/icons";
import { Flex } from "antd";

interface ViewOwnKPIProps {
  open: boolean;
  onClose: () => void;
  planName?: string;
  mappingId?: string;
  source?: "OWN" | "TEAM";
}

export default function ViewOwnKPI({
  open,
  onClose,
  planName,
  mappingId,
  source = "TEAM",
}: ViewOwnKPIProps) {
  const isTeam = source === "TEAM";

  // Details queries
  const { data: ownDetailRes, isFetching: isOwnDetailFetching } =
    useGetOwnKPIDetailsQuery(mappingId || "", {
      skip: !mappingId || isTeam || !open,
    });

  const { data: teamDetailRes, isFetching: isTeamDetailFetching } =
    useGetTeamKPIDetailsQuery(mappingId || "", {
      skip: !mappingId || !isTeam || !open,
    });

  const detailRes = isTeam ? teamDetailRes : ownDetailRes;
  const isFetching = isTeam ? isTeamDetailFetching : isOwnDetailFetching;

  const kpis = detailRes?.data?.employeeKpis || [];
  const managerMappings = detailRes?.data?.managerMappings || [];
  const attachments = detailRes?.data?.attachments || [];

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={onClose}
      title={`${planName} - View KPI`}
    >
      <WithLoader
        isLoading={isFetching}
        className={cn("rounded-lg", isFetching && "h-[calc(100vh_-_105px)]")}
      >
        <div className="space-y-8">
          {/* KPI List */}
          <div className="border border-theme-gray-700 rounded-xl p-6">
            <h4 className="text-white text-base font-semibold mb-6">
              KPI List
            </h4>
            <div className="space-y-6">
              {kpis.map((kpi: any, index: number) => (
                <div key={kpi.id || index} className="space-y-4">
                  {index > 0 && (
                    <div className="border-t border-dashed border-theme-gray-700 pt-6" />
                  )}
                  <Flex justify="space-between" align="center">
                    <h5 className="text-white text-sm font-medium">
                      KPI -{index + 1}
                    </h5>
                  </Flex>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block">
                        Name
                      </label>
                      <div className="bg-theme-slate-900 border border-theme-gray-700 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px]">
                        {kpi.kpiName}
                      </div>
                    </div>
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block">
                        Weightage(%)
                      </label>
                      <div className="bg-theme-slate-900 border border-theme-gray-700 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px]">
                        {kpi.weightage}%
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-theme-gray-400 text-xs mb-1.5 block">
                      Details
                    </label>
                    <div className="bg-theme-slate-900 border border-theme-gray-700 rounded-lg px-4 py-3 text-white text-sm min-h-[100px] whitespace-pre-wrap">
                      {kpi.details || "No details provided"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Manager Comments */}
          {managerMappings.some((m: any) => m.kpiComment) && (
            <div className="border border-theme-gray-700 rounded-xl p-6 space-y-4">
              <h4 className="text-white text-base font-semibold">
                Manager Comments
              </h4>
              {managerMappings.map((mapping: any, idx: number) => {
                if (!mapping.kpiComment) return null;
                const name = mapping.manager
                  ? `${mapping.manager.firstName || ""} ${mapping.manager.lastName || ""}`.trim()
                  : "";
                return (
                  <div key={mapping.id || idx}>
                    {idx > 0 && (
                      <div className="border-t border-dashed border-theme-gray-700 pt-4" />
                    )}
                    <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
                      Line Manager - {idx + 1} Comment{name ? ` (${name})` : ""}
                    </label>
                    <div className="bg-theme-slate-900 border border-theme-gray-700 rounded-lg px-4 py-3 text-white text-sm min-h-[80px] whitespace-pre-wrap">
                      {mapping.kpiComment}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Attachment */}
          <div>
            <h4 className="text-white text-base font-semibold mb-4">
              Attachment
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {attachments.length > 0 ? (
                attachments.map((file: any, index: number) => (
                  <a
                    key={index}
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-gray-700 hover:border-theme-primary hover:bg-slate-700 transition-all duration-200 group no-underline"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-theme-primary/10 flex-shrink-0">
                      <PaperclipIcon size={16} className="text-theme-primary" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-white text-sm font-medium group-hover:text-theme-primary transition-colors duration-200 truncate">
                        {file.fileName || "attachment.pdf"}
                      </span>
                      <span className="text-gray-500 text-xs">
                        Click to download
                      </span>
                    </div>
                  </a>
                ))
              ) : (
                <div className="col-span-full flex items-center gap-3 w-full px-4 py-2.5 rounded-lg bg-slate-800/50 border border-gray-700 opacity-60 cursor-not-allowed group no-underline">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-700/30 flex-shrink-0">
                    <PaperclipIcon size={16} className="text-gray-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-400 text-sm font-medium">
                      No attachment
                    </span>
                    <span className="text-gray-600 text-xs">Not available</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </WithLoader>
    </CustomDrawer>
  );
}
